import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Search,
  ArrowRight,
  Store,
  Star,
  MapPin,
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  Users,
  TrendingUp,
  Handshake,
  Eye,
  Sparkles,
  HeartHandshake,
  Check,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VersionSwitcher from "@/components/VersionSwitcher";
import { slugForCategory } from "@/lib/categoryHeroes";
import { CATEGORY_IMAGES } from "@/lib/categoryImages";
import { resolveAssetUrl } from "@/lib/assetUrl";
import CategoryRail from "@/components/CategoryRail";

// Hero carousel images — identical asset pool as V1 for visual parity
const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1739298061709-cfc57e6b620e?w=1200&q=80",
    caption: "Building Community Together",
    description: "Black entrepreneurs networking and collaborating",
  },
  {
    url: "https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?w=1200&q=80",
    caption: "Women Leading the Way",
    description: "Black women business professionals in collaboration",
  },
  {
    url: "https://images.unsplash.com/photo-1716399233137-5f18a8e3520d?w=1200&q=80",
    caption: "Men Making Moves",
    description: "Black men entrepreneurs closing deals",
  },
  {
    url: `${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/categories/hero-dining.jpg`,
    caption: "Dining & Hospitality",
    description: "Black-owned fine dining and hospitality experiences",
  },
  {
    url: "https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?w=1200&q=80",
    caption: "Collaborative Success",
    description: "Black professionals collaborating in business meeting",
  },
  {
    url: "https://images.unsplash.com/photo-1653565685060-e15e492a7fda?w=1200&q=80",
    caption: "Leading & Inspiring",
    description: "Black leaders presenting and inspiring teams",
  },
];

const HeroCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goToPrevious = () =>
    setCurrentIndex((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  const goToNext = () => setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-tbc-gold/20 rounded-3xl transform rotate-3"></div>
      <div className="relative rounded-2xl overflow-hidden h-[400px] lg:h-[500px]">
        {HERO_IMAGES.map((image, index) => (
          <div
            key={image.url}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <img src={image.url} alt={image.caption} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h3 className="font-heading text-2xl mb-1">{image.caption}</h3>
              <p className="text-white/80 text-sm font-body">{image.description}</p>
            </div>
          </div>
        ))}

        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="v2-carousel-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="v2-carousel-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
          {HERO_IMAGES.map((image, index) => (
            <button
              key={`dot-${image.url}`}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? "bg-tbc-gold w-6" : "bg-white/50"
              }`}
              data-testid={`v2-carousel-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function LandingPageV2() {
  const { user, login } = useAuth();
  const [categories, setCategories] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [foundingCount, setFoundingCount] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, bizRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/businesses?limit=6`),
      ]);
      setCategories(catRes.data.categories || []);
      setFeaturedBusinesses(bizRes.data.businesses || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching data:", error);
    }

    // Founding-member count via /community/stats (best-effort)
    try {
      const fmRes = await axios.get(`${API}/community/stats`);
      if (typeof fmRes.data?.founding_members === "number") {
        setFoundingCount(fmRes.data.founding_members);
      }
    } catch {
      /* ignore */
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("search", searchQuery);
    if (locationQuery.trim()) params.set("location", locationQuery);
    window.location.href = `/directory?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page-v2">
      <Navbar />

      {/* ===================== HERO ===================== */}
      <section className="relative min-h-[85vh] flex items-center">
        <div className="absolute inset-0 bg-tbc-cream grain-texture"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 space-y-8 animate-fadeIn">
              <div className="space-y-4">
                <span
                  className="inline-block px-4 py-2 bg-tbc-gold/20 text-tbc-navy font-medium text-sm tracking-wide rounded-full"
                  data-testid="v2-hero-eyebrow"
                >
                  BUILDING BLACK WEALTH TOGETHER
                </span>
                <h1
                  className="font-heading text-4xl sm:text-5xl lg:text-6xl text-tbc-navy tracking-tight leading-tight"
                  data-testid="v2-hero-headline"
                >
                  Discover, Support &{" "}
                  <span className="text-tbc-gold">Grow</span>{" "}
                  Black-Owned Businesses
                </h1>
                <p className="text-lg text-gray-600 font-body max-w-xl" data-testid="v2-hero-subheadline">
                  The BLK Community connects consumers with Black-owned businesses, entrepreneurs,
                  creators, and professionals across the country. Shop intentionally, build
                  meaningful connections, and help strengthen our communities through economic
                  empowerment.
                </p>
              </div>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="space-y-3 max-w-xl">
                <div className="flex items-center bg-white rounded-full shadow-lg border border-gray-100 overflow-hidden">
                  <Search className="w-5 h-5 text-gray-400 ml-4" />
                  <input
                    type="text"
                    placeholder="Search businesses or products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 px-4 py-4 text-gray-700 font-body focus:outline-none"
                    data-testid="v2-hero-search-input"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 flex items-center bg-white rounded-full shadow-lg border border-gray-100 overflow-hidden">
                    <MapPin className="w-5 h-5 text-gray-400 ml-4" />
                    <input
                      type="text"
                      placeholder="City, State, or ZIP..."
                      value={locationQuery}
                      onChange={(e) => setLocationQuery(e.target.value)}
                      className="flex-1 px-4 py-3 text-gray-700 font-body focus:outline-none"
                      data-testid="v2-hero-location-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6 font-medium"
                    data-testid="v2-hero-search-button"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* Primary + Secondary CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link to="/directory">
                  <Button
                    className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6 font-medium text-base w-full sm:w-auto"
                    data-testid="v2-hero-find-businesses"
                  >
                    Find Businesses
                  </Button>
                </Link>
                {user ? (
                  <Link to="/dashboard">
                    <Button
                      variant="outline"
                      className="border-2 border-tbc-navy text-tbc-navy hover:bg-tbc-navy hover:text-white rounded-full px-8 py-6 font-medium text-base w-full sm:w-auto"
                      data-testid="v2-hero-list-business"
                    >
                      List Your Business Free
                    </Button>
                  </Link>
                ) : (
                  <Button
                    onClick={login}
                    variant="outline"
                    className="border-2 border-tbc-navy text-tbc-navy hover:bg-tbc-navy hover:text-white rounded-full px-8 py-6 font-medium text-base w-full sm:w-auto"
                    data-testid="v2-hero-list-business"
                  >
                    List Your Business Free
                  </Button>
                )}
              </div>
            </div>

            <div className="lg:col-span-6 animate-fadeIn stagger-2 opacity-0">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== SEARCH SECTION (BAND) ===================== */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-4"
            data-testid="v2-search-heading"
          >
            Find Exactly What You&apos;re Looking For
          </h2>
          <p className="text-gray-600 font-body text-lg max-w-2xl mx-auto">
            Search our growing directory of Black-owned businesses by category, location, or
            service. Whether you&apos;re looking for a restaurant, realtor, contractor, consultant,
            artist, or local entrepreneur, we&apos;ve got you covered.
          </p>
        </div>
      </section>

      {/* ===================== CATEGORY RAIL ===================== */}
      <CategoryRail />

      {/* ===================== COMMUNITY MISSION ===================== */}
      <section className="py-20 bg-tbc-cream grain-texture">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2
              className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-4"
              data-testid="v2-mission-heading"
            >
              More Than A Directory. A Movement.
            </h2>
            <p className="text-gray-700 font-body text-lg max-w-3xl mx-auto">
              Every dollar spent within our community creates opportunities, supports families, and
              helps build generational wealth. The BLK Community exists to make it easier to
              discover, support, and celebrate Black excellence every day.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: HeartHandshake,
                title: "Community First",
                text:
                  "Supporting businesses that reinvest in our neighborhoods and create opportunities for future generations.",
              },
              {
                icon: TrendingUp,
                title: "Economic Empowerment",
                text:
                  "Helping entrepreneurs increase visibility, attract customers, and grow sustainable businesses.",
              },
              {
                icon: Handshake,
                title: "Connection & Collaboration",
                text:
                  "Building relationships between business owners, consumers, professionals, and community leaders.",
              },
            ].map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                className={`bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow animate-fadeIn stagger-${i + 1} opacity-0`}
                data-testid={`v2-mission-feature-${i + 1}`}
              >
                <div className="w-14 h-14 bg-tbc-gold/15 rounded-xl flex items-center justify-center mb-5">
                  <Icon className="w-7 h-7 text-tbc-gold" />
                </div>
                <h3 className="font-heading text-xl text-tbc-navy mb-2">{title}</h3>
                <p className="text-gray-600 font-body">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CATEGORIES GRID ===================== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-4"
              data-testid="v2-category-heading"
            >
              Explore Categories
            </h2>
            <p className="text-gray-600 font-body max-w-2xl mx-auto">
              Browse businesses across a wide range of industries and discover trusted products,
              services, and professionals within our community.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.slice(0, 10).map((category, index) => {
              const heroSlug = slugForCategory(category);
              const to = heroSlug
                ? `/category/${heroSlug}`
                : `/directory?category=${encodeURIComponent(category)}`;
              return (
                <Link
                  key={category}
                  to={to}
                  className={`group relative overflow-hidden rounded-xl aspect-square animate-fadeIn stagger-${(index % 5) + 1} opacity-0`}
                  data-testid={`v2-category-${category.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <img
                    src={CATEGORY_IMAGES[category] || CATEGORY_IMAGES["Other"]}
                    alt={category}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy/80 via-tbc-navy/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-body font-medium text-sm tracking-wide">
                      {category}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== FEATURED BUSINESSES ===================== */}
      <section className="py-20 bg-tbc-cream grain-texture">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2
                className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-2"
                data-testid="v2-featured-heading"
              >
                Featured Businesses
              </h2>
              <p className="text-gray-600 font-body">
                Discover outstanding entrepreneurs and businesses making a positive impact in their
                communities.
              </p>
            </div>
            <Link
              to="/directory"
              className="hidden sm:flex items-center gap-2 text-tbc-navy font-medium hover:text-tbc-gold transition-colors"
              data-testid="v2-view-all-businesses"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredBusinesses.length > 0 ? (
              featuredBusinesses.map((business, index) => (
                <Link
                  key={business.business_id}
                  to={`/business/${business.business_id}`}
                  className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 animate-fadeIn stagger-${(index % 3) + 1} opacity-0`}
                  data-testid={`v2-business-card-${business.business_id}`}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={
                        resolveAssetUrl(business.image_url) ||
                        CATEGORY_IMAGES[business.category] ||
                        CATEGORY_IMAGES["Other"]
                      }
                      alt={business.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium text-tbc-navy rounded-full">
                        {business.category === "Other" && business.custom_category
                          ? business.custom_category
                          : business.category}
                      </span>
                      {business.is_verified && (
                        <span className="px-2 py-1 bg-tbc-gold text-tbc-navy text-xs font-medium rounded-full flex items-center gap-1">
                          <BadgeCheck className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-xl text-tbc-navy mb-1 group-hover:text-tbc-gold transition-colors">
                      {business.name}
                    </h3>
                    {business.bc_score > 0 && (
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star
                            key={`star-${i}`}
                            className={`w-3 h-3 ${
                              i < Math.floor(business.bc_score)
                                ? "fill-tbc-gold text-tbc-gold"
                                : "text-gray-300"
                            }`}
                          />
                        ))}
                        <span className="text-xs text-gray-500 ml-1">{business.bc_score}/5</span>
                      </div>
                    )}
                    <p className="text-gray-600 text-sm font-body line-clamp-2">
                      {business.description}
                    </p>
                    {business.city && business.state && (
                      <p className="text-gray-400 text-xs mt-3 font-body flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {business.city}, {business.state}
                      </p>
                    )}
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-body">
                  No businesses yet. Be the first to register!
                </p>
                {!user && (
                  <Button
                    onClick={login}
                    className="mt-4 bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6"
                    data-testid="v2-register-first-business"
                  >
                    Register Your Business
                  </Button>
                )}
              </div>
            )}
          </div>

          <Link
            to="/directory"
            className="sm:hidden flex items-center justify-center gap-2 text-tbc-navy font-medium hover:text-tbc-gold transition-colors mt-8"
          >
            View All Businesses <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ===================== BUSINESS OWNER CTA ===================== */}
      <section className="py-20 bg-tbc-navy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <h2
                className="font-heading text-3xl sm:text-4xl text-white"
                data-testid="v2-owner-cta-heading"
              >
                Own A Black-Owned Business?
              </h2>
              <p className="text-gray-300 font-body text-lg">
                Join The BLK Community and connect with customers actively seeking to support
                Black-owned businesses. Create your profile, showcase your products and services,
                and become part of a growing national network.
              </p>
              <ul className="space-y-3 pt-2">
                {[
                  "Increase visibility",
                  "Attract new customers",
                  "Build community connections",
                  "Promote products and services",
                  "Join a growing business network",
                ].map((benefit) => (
                  <li
                    key={benefit}
                    className="flex items-center gap-3 text-gray-100 font-body"
                    data-testid={`v2-owner-benefit-${benefit.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="w-6 h-6 rounded-full bg-tbc-gold/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-tbc-gold" />
                    </span>
                    {benefit}
                  </li>
                ))}
              </ul>

              <div className="pt-2">
                {user ? (
                  <Link to="/dashboard">
                    <Button
                      className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                      data-testid="v2-owner-cta-button"
                    >
                      Claim Your Business Profile
                    </Button>
                  </Link>
                ) : (
                  <Button
                    onClick={login}
                    className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                    data-testid="v2-owner-cta-button"
                  >
                    Claim Your Business Profile
                  </Button>
                )}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-3 bg-tbc-gold/20 rounded-3xl transform -rotate-3"></div>
                <img
                  src="https://images.unsplash.com/photo-1716399233137-5f18a8e3520d?w=900&q=80"
                  alt="Black business owners"
                  className="relative w-full h-80 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHY JOIN ===================== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2
              className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-4"
              data-testid="v2-why-join-heading"
            >
              Why The BLK Community?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: HeartHandshake,
                title: "Support With Purpose",
                text:
                  "Make it easy to intentionally support Black-owned businesses in your local community and across the country.",
              },
              {
                icon: Eye,
                title: "Discover Hidden Gems",
                text:
                  "Find amazing entrepreneurs, creators, professionals, restaurants, and service providers you may never have found otherwise.",
              },
              {
                icon: Users,
                title: "Strengthen Our Communities",
                text:
                  "Every connection, referral, purchase, and partnership helps create lasting economic impact.",
              },
            ].map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                className={`text-center p-8 rounded-2xl bg-tbc-cream/50 animate-fadeIn stagger-${i + 1} opacity-0`}
                data-testid={`v2-why-join-feature-${i + 1}`}
              >
                <div className="w-16 h-16 bg-tbc-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Icon className="w-8 h-8 text-tbc-gold" />
                </div>
                <h3 className="font-heading text-xl text-tbc-navy mb-3">{title}</h3>
                <p className="text-gray-600 font-body">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FOUNDER STORY ===================== */}
      <section className="py-20 bg-tbc-cream grain-texture">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="font-heading text-3xl sm:text-4xl text-tbc-navy mb-6"
            data-testid="v2-founder-heading"
          >
            Built For Community. Driven By Purpose.
          </h2>
          <div className="space-y-5 text-gray-700 font-body text-lg">
            <p>
              The BLK Community was created with a simple mission: make it easier for people to
              discover, support, and invest in Black-owned businesses.
            </p>
            <p>
              We believe economic empowerment starts with visibility, connection, and intentional
              support. By bringing entrepreneurs and consumers together in one trusted platform,
              we&apos;re helping strengthen businesses, families, and communities for generations to
              come.
            </p>
            <p className="font-heading text-2xl text-tbc-navy pt-3">
              Together, we&apos;re building more than a directory—we&apos;re building a legacy.
            </p>
          </div>
        </div>
      </section>

      {/* ===================== FOUNDING MEMBER ===================== */}
      <section className="py-20 bg-tbc-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-tbc-gold/15 text-tbc-gold font-medium text-xs tracking-[0.25em] rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            FOUNDING MEMBERS PROGRAM
          </div>
          <h2
            className="font-heading text-3xl sm:text-4xl text-white mb-5"
            data-testid="v2-founding-heading"
          >
            Become A Founding Member
          </h2>
          <p className="text-gray-300 font-body text-lg max-w-2xl mx-auto mb-3">
            Join the first 100 businesses helping shape the future of The BLK Community.
          </p>
          <p className="text-gray-400 font-body max-w-2xl mx-auto mb-8">
            Founding members receive early visibility, priority placement, exclusive networking
            opportunities, and recognition as supporters of the movement from day one.
          </p>

          {/* No live counter — capped at 100, first come first served */}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/founding-members">
              <Button
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                data-testid="v2-founding-cta"
              >
                Become A Founding Member
              </Button>
            </Link>
            <Link to="/directory">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-tbc-navy rounded-full px-8 py-6 font-medium text-lg"
                data-testid="v2-founding-browse"
              >
                Browse Directory
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== FOOTER TAGLINE BAND ===================== */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-3">
          <p
            className="font-heading text-xl sm:text-2xl text-tbc-navy"
            data-testid="v2-footer-tagline-short"
          >
            Supporting Black Excellence. Building Community. Creating Legacy.
          </p>
          <p className="text-gray-600 font-body max-w-3xl mx-auto">
            The BLK Community is a marketplace and business directory dedicated to connecting
            consumers with Black-owned businesses while fostering economic growth, collaboration,
            and community empowerment.
          </p>
        </div>
      </section>

      <Footer />
      <VersionSwitcher />
    </div>
  );
}
